// ---------------------------------------------------------------------------
// AgentForm.validation — pure validity checks for the agent form. Produces a
// per-field error map plus an overall `isValid` flag used to gate submit.
// ---------------------------------------------------------------------------

import { isValidAgentName } from '../../../types/agent.js';

import type {
  AgentFormMode,
  AgentFormState,
  VariableRow,
} from './AgentForm.types.js';
import { VARIABLE_NAME_PATTERN } from './AgentForm.types.js';
import {
  collectDeclaredVarNames,
  collectReferencedVars,
  isVariableRowEmpty,
} from './AgentForm.utils.js';

export interface AgentFormErrors {
  name?: string;
  modelName?: string;
  systemPrompt?: string;
  userMessage?: string;
  // rowId -> message
  variables: Record<string, string>;
  /** `$var` names referenced in the spec text but not declared as variables. */
  undeclaredVars: string[];
}

export interface AgentFormValidation {
  errors: AgentFormErrors;
  isValid: boolean;
}

function validateVariableRows(rows: VariableRow[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const seen = new Set<string>();
  for (const row of rows) {
    if (isVariableRowEmpty(row)) continue;
    const name = row.name.trim();
    if (name === '') {
      errors[row.id] = 'Name is required.';
      continue;
    }
    if (!VARIABLE_NAME_PATTERN.test(name)) {
      errors[row.id] = 'Use UPPER_SNAKE_CASE.';
      continue;
    }
    if (seen.has(name)) {
      errors[row.id] = 'Duplicate variable name.';
      continue;
    }
    seen.add(name);
  }
  return errors;
}

export function validateAgentForm(
  state: AgentFormState,
  mode: AgentFormMode,
): AgentFormValidation {
  const errors: AgentFormErrors = { variables: {}, undeclaredVars: [] };

  if (mode === 'create') {
    const name = state.name.trim();
    if (name === '') {
      errors.name = 'Name is required.';
    } else if (!isValidAgentName(name)) {
      errors.name =
        'Use lowercase letters, digits and hyphens. Must start with a letter.';
    }
  }

  if (state.modelName.trim() === '') {
    errors.modelName = 'Model name is required.';
  }
  if (state.systemPrompt.trim() === '') {
    errors.systemPrompt = 'System prompt is required.';
  }
  if (state.userMessage.trim() === '') {
    errors.userMessage = 'User message is required.';
  }

  errors.variables = validateVariableRows(state.variables);

  // Every `{{$var:NAME}}` used in the spec must be declared as a variable, so
  // the value carries over to eval templates (which bind declared variables).
  const declaredNames = collectDeclaredVarNames(state.variables);
  errors.undeclaredVars = collectReferencedVars(state).filter(
    (name) => !declaredNames.has(name),
  );

  const isValid =
    errors.name === undefined &&
    errors.modelName === undefined &&
    errors.systemPrompt === undefined &&
    errors.userMessage === undefined &&
    Object.keys(errors.variables).length === 0 &&
    errors.undeclaredVars.length === 0;

  return { errors, isValid };
}
